import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  wasOffline: boolean;
  isSlowConnection: boolean;
}

/**
 * Hook to monitor network connectivity status
 * Detects online/offline state and connection quality
 */
export const useNetworkStatus = (): NetworkStatus => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [wasOffline, setWasOffline] = useState<boolean>(false);
  const [isSlowConnection, setIsSlowConnection] = useState<boolean>(false);

  // Test connection speed by making a lightweight request
  const checkConnectionSpeed = useCallback(async () => {
    // Only check if we're online
    if (!navigator.onLine) {
      setIsSlowConnection(false);
      return;
    }

    try {
      const startTime = Date.now();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      // Try a lightweight request - use a common endpoint or just check connectivity
      // Using a small image or favicon as a lightweight check
      await fetch('/favicon.ico', {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache',
      }).catch(() => {
        // If favicon doesn't exist, try a simple API endpoint
        return fetch('/api/server-time', {
          method: 'HEAD',
          signal: controller.signal,
          cache: 'no-cache',
        });
      });

      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      setIsSlowConnection(duration > 2000); // Consider slow if > 2 seconds
    } catch (error) {
      // If health check fails, don't assume slow - might just be endpoint not available
      // Only mark as slow if we get a network error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setIsSlowConnection(true);
      }
    }
  }, []);

  useEffect(() => {
    // Initial connection speed check
    if (isOnline) {
      checkConnectionSpeed();
    }

    const handleOnline = () => {
      setIsOnline(true);
      setWasOffline(true);
      // Check connection speed when coming back online
      checkConnectionSpeed();
      // Reset wasOffline after a short delay
      setTimeout(() => setWasOffline(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      setIsSlowConnection(false);
    };

    // Listen to browser online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic connection check (every 30 seconds when online)
    const intervalId = setInterval(() => {
      if (navigator.onLine !== isOnline) {
        if (navigator.onLine) {
          handleOnline();
        } else {
          handleOffline();
        }
      }
      
      // Check connection speed periodically when online
      if (navigator.onLine) {
        checkConnectionSpeed();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, [isOnline, checkConnectionSpeed]);

  return {
    isOnline,
    wasOffline,
    isSlowConnection,
  };
};

